<template>
	<div
		class="right-side-drawer display-flex"
		:class="[
			sidebarClass,
			{
				'sidebar-open': sideBarState !== 'closed' && !forceFullWidth,
				'width-100': forceFullWidth,
				'full-width': isWideWidget && !forceFullWidth,
				'widget-pdf-viewer-max-width': isFileViewerActive && !forceFullWidth,
				'narrow-widget-max-width': isWidget && !isWideWidget && !isFileViewerActive && !forceFullWidth,
			},
		]"
	>
		<v-fade-transition>
			<div class="height-100 width-100">
				<slot />
				<ShrinkButton @click="$emit('closeSideBar')" class="close-btn" />
			</div>
		</v-fade-transition>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

import ShrinkButton from './ShrinkButton.vue';
import { isMiniApp } from '@/store/modules/selectedNode/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { widgetMetadata } from '@/store/modules/widgetEditor/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WidgetService from '@/utils/WidgetService';
import WorkflowService from '@/utils/WorkflowService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'RightSideDrawer',
	props: {
		sideBarState: { type: String, required: true },
		editNode: { type: Object, required: true },
	},
	components: { ShrinkButton },
	computed: {
		...mapGettersShorthand({ editWorkflow }),
		...mapGetters([isMiniApp.fullPath, widgetMetadata.fullPath]),
		miniAppType() {
			if (this.isMiniApp) {
				return this.editNode.params_map.mini_app_type;
			}
			return null;
		},
		shouldShowWidgetLayoutSettings() {
			return WidgetService.shouldShowWidgetLayoutSettings({ miniAppType: this.miniAppType, isMiniApp: this.isMiniApp });
		},
		forceFullWidth() {
			return this.sideBarState === 'force-full-width';
		},
		isTileWorkflow() {
			return WorkflowService.isOfTypeTile(this.editWorkflow);
		},
		sidebarClass() {
			if (!this.isEditing && this.sideBarState !== 'trigger') {
				return 'flex-0';
			}
			if (this.sideBarState === 'widget') {
				if (this.isTileWorkflow) {
					return 'tile-editor';
				} else {
					return 'flex-5';
				}
			} else if (this.sideBarState === 'force-full-width') {
				return 'width-100';
			} else if (this.sideBarState === 'tested-widget') {
				return 'is-mini-app';
			} else if (this.sideBarState === 'mini-app') {
				return 'flex-4';
			} else if (this.sideBarState === 'notification') {
				return 'notification-editor';
			}
			return 'app-select-width';
		},
		isEditing() {
			return Object.keys(this.editNode).length > 0;
		},
		isWidget() {
			return WorkflowNodeService.isWidget(this.editNode);
		},
		isMiniAppWideCompatible() {
			return WidgetService.shouldShowWidgetLayoutSettings({
				miniAppType: this.miniAppType,
				isMiniApp: this.isMiniApp,
			});
		},
		isWideWidget() {
			if (this.sideBarState === 'widget' || this.isMiniAppWideCompatible) {
				return this.widgetOptions.is_wide?.active || false;
			} else {
				return false;
			}
		},
		isFileViewerActive() {
			return this.isWidget && this.widgetOptions.file_viewer?.active;
		},
		widgetOptions() {
			return this[widgetMetadata.fullPath]?.options || {};
		},
	},
};
</script>

<style scoped lang="scss">
$flow-tree-width-on-wide-mode: 60px;

// .full-width {
// 	width: calc(100% - #{$flow-tree-width-on-wide-mode});
// }
.right-side-drawer {
	position: relative;

	background: var(--bg-color) !important;
	box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.2) !important;
	z-index: $z-index-drawer;

	&.sidebar-open {
		.close-btn {
			opacity: 1;
			transition: 1s;
		}
	}

	&:not(.sidebar-open) {
		::v-deep(.expandable-sliding-drawer) {
			width: 0 !important;
			overflow: hidden;
		}
		.close-btn {
			opacity: 0;
			transition: 1s;
		}
	}

	&.app-select-width {
		min-width: var(--base-width);
		max-width: 80%;
	}
	&.full-width {
		width: calc(100% - #{$flow-tree-width-on-wide-mode});
	}
	&.widget-pdf-viewer-max-width {
		max-width: 1050px;
	}
	&.narrow-widget-max-width {
		max-width: calc(var(--widget-form-width) + var(--widget-field-settings-editor) + 80px);
	}
	&.is-mini-app {
		width: calc(var(--widget-form-width) + 30px);
	}
	&.notification-editor {
		max-width: 960px;
		min-width: 960px;
	}
	&.tile-editor {
		max-width: 790px;
		min-width: 790px;
	}
	.close-btn {
		position: absolute;
		top: 20px;
		left: -60px;
	}
}
</style>
